import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject, firstValueFrom, Observable, Subject, of } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import { Application } from '../../../interfaces/application.interface';
import { ApplicationService } from '../../../services/application.service';
import { DashboardItemService } from '../../../services/dashboard-item.service';
import { LogMatcherContextService } from '../../../services/logmatcher-context.service';
import { SelectedCompanyService } from '../../../selected-company/selected-company.service';
import { ActivatedRoute, Router } from '@angular/router';
import { JobLogPatternService, UnmatchedGroupDto, UnmatchedJobLogDto } from '@asamco/application-link/shared';

type DatePreset = 'last-7-days' | 'last-14-days' | 'last-30-days' | 'all' | 'custom';

@Component({
  selector: 'appshell-link-joblog-unmatched-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, AsyncPipe],
  templateUrl: './link-joblog-unmatched-admin.component.html',
  styleUrl: './link-joblog-unmatched-admin.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LinkJoblogUnmatchedAdmin {
	readonly datePresetOptions: { value: DatePreset; label: string }[] = [
		{ value: 'last-7-days', label: 'Last week' },
		{ value: 'last-14-days', label: 'Last 2 weeks' },
		{ value: 'last-30-days', label: 'Last month' },
		{ value: 'all', label: 'All time' },
		{ value: 'custom', label: 'Custom range' },
	];

  // ===== dropdowns =====
  readonly jobLogTypeOptions = [
    { value: null as number | null, label: 'All (no filter)' },
    { value: 0, label: 'Undefined' },
    { value: 1, label: 'Success' },
    { value: 2, label: 'Fail' },
    { value: 3, label: 'AmountMismatch' },
    { value: 4, label: 'Error' },
    { value: 5, label: 'All' }, // normalize to null when searching
    { value: 6, label: 'Info' },
  ] as const;

  // ===== form filters (ngModel) =====
  take = 2000;
  linkId: number | null = null;
  jobLogType: number | null = 4;
  fromApplicationId: number | null = null;
  toApplicationId: number | null = null;
	datePreset: DatePreset = 'last-14-days';
	fromDate = '';
	toDate = '';
	messagePattern = '';

  // ===== UI state =====
  readonly loading$ = new BehaviorSubject<boolean>(false);
  readonly error$ = new BehaviorSubject<string>('');
  readonly copied$ = new BehaviorSubject<string>(''); // simple feedback message

  // Trigger a search
  private readonly searchClick$ = new Subject<void>();

  readonly applications$: Observable<Application[]> = this.applicationService.get().pipe(
    map(apps =>
      [...(apps ?? [])].sort((a, b) =>
        (a.displayName || a.name || '').localeCompare(b.displayName || b.name || '', undefined, {
          sensitivity: 'base',
        })
      )
    ),
    catchError(() => of([])),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly groups$: Observable<UnmatchedGroupDto[]> = this.searchClick$.pipe(
    startWith(void 0), // optional: load once on entry; remove if you want "manual search only"
    switchMap(() => {
      this.loading$.next(true);
      this.error$.next('');
      this.copied$.next('');

      const take = this.clampTake(this.take);
      const jobLogType = this.normalizedJobLogType;

      return this.jobLogPatternService
        .search({
          take,
          linkId: this.linkId,
          jobLogType,
          fromApplicationId: this.fromApplicationId,
          toApplicationId: this.toApplicationId,
				fromUtc: this.resolvedFromUtc,
				toUtc: this.resolvedToUtc,
				messagePattern: this.normalizedMessagePattern,
        })
        .pipe(
          catchError(() => {
            this.error$.next('Failed to load unmatched logs.');
            return of([] as UnmatchedGroupDto[]);
          }),
          tap(() => this.loading$.next(false))
        );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  constructor(
    private jobLogPatternService: JobLogPatternService,
    private applicationService: ApplicationService,
    private dashboardItemService: DashboardItemService,
    private router: Router,
    private route: ActivatedRoute,
    private selectedCompanyService: SelectedCompanyService,
    private ctx: LogMatcherContextService
  ) { }

  // Normalize once
  get normalizedJobLogType(): number | null {
    return this.jobLogType === 5 ? null : this.jobLogType;
  }

	get isCustomDateRange(): boolean {
		return this.datePreset === 'custom';
	}

	get normalizedMessagePattern(): string | null {
		const value = this.messagePattern.trim();
		return value ? value : null;
	}

	get resolvedFromUtc(): string | null {
		const range = this.resolveDateRange();
		return range?.fromUtc ?? null;
	}

	get resolvedToUtc(): string | null {
		const range = this.resolveDateRange();
		return range?.toUtc ?? null;
	}

  goAnalysisFromUnmatched(g: UnmatchedGroupDto) {
    if (!g?.message) return;

    // Persist filters so Analysis hydrates with the same slice constraints
    this.ctx.filters$.next({
      linkId: this.linkId,
      jobLogType: this.normalizedJobLogType, // keep 5 => null normalization
      fromApplicationId: this.fromApplicationId,
      toApplicationId: this.toApplicationId,
      take: 100,          // or keep your Analysis default; adjust if you want
      enforceOrder: true, // or carry over if you add this toggle to Unmatched later
    });

    // Seed the unmatched message for the Analysis screen
    this.ctx.seedMessage$.next(g.message);

    // Navigate to the Analysis route
    this.router.navigate(['../analysis'], {
      relativeTo: this.route,
      queryParams: { seed: 1 },
    });
  }

  onGroupKeydown(e: KeyboardEvent, g: UnmatchedGroupDto) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.goAnalysisFromUnmatched(g);
    }
  }

	async openJobLog(jobLog: UnmatchedJobLogDto, event?: Event) {
		event?.stopPropagation();

		if (!jobLog?.linkId) return;

		try {
			const dashboardItems = await firstValueFrom(this.dashboardItemService.adminGetAll());
			const companyId = (dashboardItems.find(item => item.id === jobLog.linkId) as { companyId?: number } | undefined)?.companyId;

			if (!companyId) {
				this.error$.next('Failed to resolve selected log company.');
				return;
			}

			const switchedCompany = await this.selectedCompanyService.setCompanyId(companyId);
			if (!switchedCompany) {
				this.error$.next('Failed to switch to the selected log company.');
				return;
			}

			await this.router.navigate(['application-link/link/' + jobLog.linkId], {
				queryParams: {
					tab: 4,
					instanceId: jobLog.instanceId ?? null,
					jobLogId: jobLog.id,
				},
			});
		} catch {
			this.error$.next('Failed to open selected log.');
		}
  }

  onJobLogKeydown(e: KeyboardEvent, jobLog: UnmatchedJobLogDto) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
			void this.openJobLog(jobLog);
    }
  }

  private clampTake(v: number): number {
    const n = Number(v);
    if (!Number.isFinite(n)) return 2000;
    // pick a sensible max to avoid perf issues
    return Math.max(10, Math.min(10000, Math.floor(n)));
  }

  search() {
    // Optional: if take was invalid, fix it in the input too
    this.take = this.clampTake(this.take);
    this.searchClick$.next();
  }

  clear() {
    this.linkId = null;
    this.jobLogType = null;
    this.fromApplicationId = null;
    this.toApplicationId = null;
    this.take = 2000;
		this.datePreset = 'last-14-days';
		this.fromDate = '';
		this.toDate = '';
		this.messagePattern = '';

    this.error$.next('');
    this.copied$.next('');
    // keep results visible or clear them? currently keeping last results (better UX)
    // if you want to clear results visually, you can trigger a search with defaults:
    // this.searchClick$.next();
  }

  async copy(text: string) {
    if (!text) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // fallback
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.copied$.next('Copied to clipboard.');
      setTimeout(() => this.copied$.next(''), 1200);
    } catch {
      this.copied$.next('Copy failed.');
      setTimeout(() => this.copied$.next(''), 1500);
    }
  }

  exportCsv(groups: UnmatchedGroupDto[]) {
    if (!groups?.length) return;

    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;

    const header = ['count', 'lastSeenUtc', 'message'].join(',');
    const lines = groups.map(g => [esc(g.count), esc(g.lastSeenUtc), esc(g.message)].join(','));
    const csv = [header, ...lines].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `logmatcher-unmatched-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();

    URL.revokeObjectURL(url);
  }

  trackByMessage(_: number, g: UnmatchedGroupDto) {
    return g.message;
  }

  trackByJobLogId(_: number, jobLog: UnmatchedJobLogDto) {
    return jobLog.id;
  }

	private resolveDateRange(): { fromUtc: string | null; toUtc: string | null } | null {
		if (this.datePreset === 'all') {
			return { fromUtc: null, toUtc: null };
		}

		if (this.datePreset === 'custom') {
			return {
				fromUtc: this.toStartOfDayUtc(this.fromDate),
				toUtc: this.toEndOfDayUtc(this.toDate),
			};
		}

		const now = new Date();
		const end = new Date(Date.UTC(
			now.getUTCFullYear(),
			now.getUTCMonth(),
			now.getUTCDate(),
			23,
			59,
			59,
			999
		));

		const days = this.datePreset === 'last-7-days'
			? 7
			: this.datePreset === 'last-30-days'
				? 30
				: 14;

		const start = new Date(end);
		start.setUTCDate(start.getUTCDate() - (days - 1));
		start.setUTCHours(0, 0, 0, 0);

		return {
			fromUtc: start.toISOString(),
			toUtc: end.toISOString(),
		};
	}

	private toStartOfDayUtc(value: string): string | null {
		if (!value) return null;
		return `${value}T00:00:00.000Z`;
	}

	private toEndOfDayUtc(value: string): string | null {
		if (!value) return null;
		return `${value}T23:59:59.999Z`;
	}
}
